import {DiagramType, DiagramWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {useDiagramWidgetUpdate} from "./useDiagramWidgetUpdate"
import {useAutoSave} from "../../hooks/useAutoSave"
import './DiagramWidgetFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: DiagramWidgetData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const DiagramWidgetFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const [editingText, setEditingText] = useState(data.text)
    const [editingType, setEditingType] = useState<DiagramType>(data.diagramType)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text)
        setEditingType(data.diagramType)
    }, [data.text, data.diagramType])

    const {updateData} = useDiagramWidgetUpdate({canvasId, widgetId})

    const saveText = () => {
        if (editingText !== data.text || editingType !== data.diagramType) {
            updateData(['text', 'diagramType', 'hideType'], {...data, text: editingText, diagramType: editingType})
        }
    }

    useAutoSave({saveFn: saveText})

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }

    const handleTypeChange = (type: DiagramType) => {
        setEditingType(type)
    }

    const handleHideTypeToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const newHideType = !data.hideType
        updateData(['hideType'], {...data, hideType: newHideType})
    }

    const renderTypeButton = (type: DiagramType) => (
        <button
            key={type}
            className={`diagram-widget-full-editor__button ${editingType === type ? 'diagram-widget-full-editor__button--active' : ''}`}
            onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleTypeChange(type)
            }}
            aria-label={`Set diagram type to ${type}`}
            type='button'
        >
            {type}
        </button>
    )

    return (
        <div className='diagram-widget-full-editor'>
            <div className='diagram-widget-full-editor__header'>
                <span className='diagram-widget-full-editor__title'>Diagram Widget Properties</span>
                <button
                    className='diagram-widget-full-editor__close'
                    onClick={onClose}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='diagram-widget-full-editor__sections'>
                <div className='diagram-widget-full-editor__section diagram-widget-full-editor__section--type' title='Diagram Type'>
                    <div className='diagram-widget-full-editor__type-group'>
                        {Object.values(DiagramType).map(renderTypeButton)}
                    </div>
                </div>

                <div className='diagram-widget-full-editor__section diagram-widget-full-editor__section--hideType' title='Hide Type'>
                    <button
                        className={`diagram-widget-full-editor__button ${data.hideType ? 'diagram-widget-full-editor__button--active' : ''}`}
                        onMouseDown={handleHideTypeToggle}
                        aria-label='Toggle hide type'
                        type='button'
                    >
                        Hide Type
                    </button>
                </div>

                <div className='diagram-widget-full-editor__section diagram-widget-full-editor__section--text' title='Source Code'>
                    <textarea
                        ref={textareaRef}
                        className='diagram-widget-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        autoFocus
                        rows={10}
                    />
                </div>
            </div>
        </div>
    )
}
